import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

import { getPhoneDigits, getPhoneLengthError, isPhoneLengthValid } from '../components/InputPhone/lib/utils';
import { usePhoneContext } from '../context/PhoneContext';
import { useVkCaptchaContext } from '../context/VkCaptchaContext';
import { useWidget } from '../context/WidgetContext';
import { setBooleanToStorage } from '../utils';

import { HAS_MAX_USER_PHONE } from '../constants/localStorage';

export const usePhoneInput = () => {
  const { phoneNumber, setPhoneNumber, selectedCountry, setShowPhoneInput } = usePhoneContext();
  const { isCaptchaPassed } = useVkCaptchaContext();
  const { requirePhone, hasMaxUserPhone, setHasMaxUserPhone, localeMessages } = useWidget();
  const [phoneError, setPhoneError] = useState('');
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    if (isCaptchaPassed && requirePhone && hasMaxUserPhone === false) {
      setShowInput(true);
    } else {
      setShowInput(false);
    }
  }, [isCaptchaPassed, requirePhone, hasMaxUserPhone]);

  useEffect(() => {
    setShowPhoneInput?.(() => {
      if (isCaptchaPassed && requirePhone && hasMaxUserPhone === false) {
        setShowInput(true);
      }
    });
  }, [isCaptchaPassed, requirePhone, hasMaxUserPhone, setShowPhoneInput]);

  const { onPhoneSubmit, onPhoneCancel } = usePhoneContext();

  const handlePhoneChange = useCallback(
    (value: string) => {
      setPhoneNumber(value);
      setPhoneError('');
    },
    [setPhoneNumber]
  );

  const handlePhoneSubmit = useCallback(() => {
    if (!phoneNumber) {
      setPhoneError(localeMessages.phone.empty);
      return;
    }

    if (!selectedCountry) {
      setPhoneError(localeMessages.phone.selectCountry);
      return;
    }

    const lengthError = getPhoneLengthError(getPhoneDigits(phoneNumber).length, selectedCountry);
    if (lengthError === 'minLength') {
      setPhoneError(localeMessages.phone.minLength(selectedCountry.minLength));
      return;
    }
    if (lengthError === 'invalidFormat') {
      setPhoneError(localeMessages.phone.invalidFormat);
      return;
    }

    setShowInput(false);

    // После успешного ввода телефона обновляем hasMaxUserPhone
    // Считаем что телефон теперь есть у пользователя
    setHasMaxUserPhone?.(true);
    setBooleanToStorage(HAS_MAX_USER_PHONE, true);

    onPhoneSubmit?.();
  }, [phoneNumber, onPhoneSubmit, selectedCountry, setHasMaxUserPhone, localeMessages]);

  const handleCancel = useCallback(() => {
    setPhoneNumber('');
    setPhoneError('');
    setShowInput(false);
    onPhoneCancel?.();
  }, [setPhoneNumber, onPhoneCancel]);

  const isButtonDisabled = useMemo(() => {
    if (!phoneNumber || !selectedCountry) {
      return true;
    }

    const digitsLength = getPhoneDigits(phoneNumber).length;
    if (digitsLength === 0) {
      return true;
    }

    return !isPhoneLengthValid(digitsLength, selectedCountry);
  }, [phoneNumber, selectedCountry]);

  return {
    phoneNumber,
    phoneError,
    showInput,
    isButtonDisabled,
    handlePhoneChange,
    handlePhoneSubmit,
    handleCancel,
  };
};
